import { createTheme, PaletteMode, alpha } from '@mui/material';

const palette = {
    aqua: {
        50: '#f3fbfb',
        100: '#e7f6f6',
        200: '#dcf2f1',
        300: '#c1e8e6',
        400: '#a6ddda',
        500: '#8bd3cf',
        600: '#70c9c3',
        700: '#55bfb8',
        800: '#3ab5ac',
        900: '#1fab9f',
    },
    sky: {
        50: '#eef7fb',
        100: '#dceff7',
        200: '#c9e7f2',
        300: '#a7d6e5',
        400: '#7fc7d9',
        500: '#5eb8cc',
        600: '#48a9bf',
        700: '#3799b2',
        800: '#2b8aa3',
        900: '#1e7b95',
    },
    navy: {
        50: '#eaeff6',
        100: '#d5dfed',
        200: '#bfcfe3',
        300: '#94aed1',
        400: '#6f8ebe',
        500: '#516fab',
        600: '#365486',
        700: '#2d4570',
        800: '#243659',
        900: '#1b2743',
    },
    midnight: {
        50: '#e4e4ef',
        100: '#c8c9df',
        200: '#acadce',
        300: '#8485b3',
        400: '#5d5d97',
        500: '#41417f',
        600: '#2b2b66',
        700: '#1f1f4d',
        800: '#141434',
        900: '#0f1035',
    },
    gray: {
        50: '#fafafa',
        100: '#f5f5f5',
        200: '#eeeeee',
        300: '#e0e0e0',
        400: '#bdbdbd',
        500: '#9e9e9e',
        600: '#757575',
        700: '#616161',
        800: '#424242',
        900: '#212121',
    }
};

export const lightTheme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            light: palette.navy[400],
            main: palette.navy[600],
            dark: palette.navy[800],
            contrastText: '#ffffff',
        },
        secondary: {
            light: palette.sky[300],
            main: palette.sky[400],
            dark: palette.sky[600],
            contrastText: '#ffffff',
        },
        info: {
            light: palette.aqua[200],
            main: palette.aqua[400],
            dark: palette.aqua[600],
            contrastText: palette.gray[800],
        },
        error: {
            main: '#f44336',
        },
        warning: {
            main: '#ff9800',
        },
        success: {
            main: '#4caf50',
        },
        background: {
            default: palette.gray[50],
            paper: '#ffffff',
        },
        text: {
            primary: palette.gray[900],
            secondary: palette.gray[700],
            disabled: palette.gray[500],
        },
        divider: palette.gray[200],
        aqua: palette.aqua,
        sky: palette.sky,
        navy: palette.navy,
        midnight: palette.midnight,
        gray: palette.gray,
    },
    components: {
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: '#ffffff',
                    borderRight: `1px solid ${palette.gray[200]}`,
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: '#ffffff',
                    color: palette.gray[900],
                    boxShadow: `0px 1px 3px ${alpha(palette.gray[900], 0.1)}`,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    boxShadow: `0px 2px 8px ${alpha(palette.gray[900], 0.1)}`,
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    borderRadius: 8,
                },
            },
        },
    },
});

export const darkTheme = createTheme({
    palette: {
        mode: 'dark',
        primary: {
            light: palette.navy[300],
            main: palette.navy[400],
            dark: palette.navy[600],
            contrastText: '#ffffff',
        },
        secondary: {
            light: palette.sky[200],
            main: palette.sky[300],
            dark: palette.sky[500],
            contrastText: '#ffffff',
        },
        info: {
            light: palette.aqua[200],
            main: palette.aqua[300],
            dark: palette.aqua[500],
            contrastText: palette.gray[900],
        },
        error: {
            main: '#f44336',
        },
        warning: {
            main: '#ff9800',
        },
        success: {
            main: '#4caf50',
        },
        background: {
            default: palette.midnight[900],
            paper: palette.midnight[800],
        },
        text: {
            primary: palette.gray[100],
            secondary: palette.gray[300],
            disabled: palette.gray[600],
        },
        divider: palette.gray[700],
        aqua: palette.aqua,
        sky: palette.sky,
        navy: palette.navy,
        midnight: palette.midnight,
        gray: palette.gray,
    },
    components: {
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: palette.midnight[800],
                    borderRight: `1px solid ${alpha(palette.gray[900], 0.5)}`,
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: palette.midnight[800],
                    boxShadow: `0px 1px 3px ${alpha('#000000', 0.3)}`,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundColor: palette.midnight[800],
                    boxShadow: `0px 2px 8px ${alpha('#000000', 0.3)}`,
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    borderRadius: 8,
                },
            },
        },
    },
});

declare module '@mui/material/styles' {
    interface Palette {
        aqua: typeof palette.aqua;
        sky: typeof palette.sky;
        navy: typeof palette.navy;
        midnight: typeof palette.midnight;
        gray: typeof palette.gray;
    }

    interface PaletteOptions {
        aqua?: Partial<typeof palette.aqua>;
        sky?: Partial<typeof palette.sky>;
        navy?: Partial<typeof palette.navy>;
        midnight?: Partial<typeof palette.midnight>;
        gray: typeof palette.gray;
    }
}

export const getTheme = (mode: PaletteMode) => {
    return mode === 'light' ? lightTheme : darkTheme;
};